import React, { useEffect, useRef, useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { FaArrowRight, FaArrowLeft, FaStar, FaAward, FaCheckCircle, FaAws, FaGoogle } from 'react-icons/fa';
import { SiSecurityscorecard, SiTrendmicro } from 'react-icons/si';
import { MdBusiness, MdLocalHospital } from 'react-icons/md';
import publicData from '../../../data/publicData.json';

const TestimonialSection = () => {
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === 'light';
  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef(null);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });
  const isMobile = windowSize.width < 768;
  const slidesToShow = isMobile ? 1 : 3;
  const { testimonials } = publicData;
  const totalSlides = testimonials.items.length;

  const trustedBrands = [
    { name: "Amazon Web Services", icon: <FaAws size={40} /> },
    { name: "Google Cloud", icon: <FaGoogle size={40} /> },
    { name: "SecureBank", icon: <SiSecurityscorecard size={40} /> },
    { name: "Trendify Fashion", icon: <SiTrendmicro size={40} /> },
    { name: "EnterpriseTech Solutions", icon: <MdBusiness size={40} /> },
    { name: "MedTech Innovations", icon: <MdLocalHospital size={40} /> }
  ];

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      const maxScroll = slider.scrollWidth - slider.clientWidth;
      const currentScroll = slider.scrollLeft;
      
      setShowLeftArrow(currentScroll > 0);
      setShowRightArrow(currentScroll < maxScroll - 10);
      
      const slideWidth = slider.clientWidth;
      const newSlide = Math.round(currentScroll / slideWidth);
      if (newSlide !== currentSlide) {
        setCurrentSlide(newSlide);
      }
    };

    slider.addEventListener('scroll', handleScroll);
    return () => slider.removeEventListener('scroll', handleScroll);
  }, [currentSlide]);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const play = () => {
      if (sliderRef.current) {
        const maxScroll = sliderRef.current.scrollWidth - sliderRef.current.clientWidth;
        const currentScroll = sliderRef.current.scrollLeft;

        if (currentScroll >= maxScroll) {
          sliderRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          const nextSlide = (currentSlide + 1) % Math.ceil(totalSlides / slidesToShow);
          scrollToSlide(nextSlide);
        }
      }
    };

    autoPlayRef.current = setInterval(play, 5000);
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [currentSlide, isAutoPlaying, totalSlides, slidesToShow]);

  const scrollToSlide = (slideIndex) => {
    const container = sliderRef.current;
    if (!container) return;

    const slideWidth = container.clientWidth;
    const scrollPosition = slideWidth * slideIndex;
    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
    setCurrentSlide(slideIndex);
  };

  const scrollDirection = (direction) => {
    if (!sliderRef.current) return;

    const container = sliderRef.current;
    const slideWidth = container.clientWidth;
    const targetScroll = direction === 'left' 
      ? container.scrollLeft - slideWidth
      : container.scrollLeft + slideWidth;

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });

    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const handleMobileSlideClick = (index) => {
    if (!sliderRef.current) return;
    
    const slideWidth = window.innerWidth * 0.85; // 85vw for mobile
    sliderRef.current.scrollTo({
      left: slideWidth * index,
      behavior: 'smooth'
    });
    setCurrentSlide(index);
    
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section 
      id="testimonials" 
      className={`py-20 ${
        isLight 
          ? 'bg-gradient-to-r from-gray-900 to-black'
          : 'bg-gradient-to-r from-gray-100 to-white'
      }`}
    >
      <motion.div
        className="container mx-auto px-4"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <motion.div 
          className="text-center max-w-4xl mx-auto mb-16"
          variants={itemVariants}
        >
          <h2 className={`text-4xl font-bold mb-6 ${
            isLight ? 'text-white' : 'text-gray-900'
          }`}>
            {testimonials.title}
          </h2>
          <p className={`text-lg ${
            isLight ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {testimonials.description}
          </p>
        </motion.div>

        <div className="relative">
          {/* Navigation Arrows */}
          <button
            onClick={() => scrollDirection('left')}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-4 rounded-full transition-all duration-300 ${
              showLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
            } ${
              isLight 
                ? 'bg-gray-800 text-white hover:bg-gray-700'
                : 'bg-white text-gray-800 hover:bg-gray-100'
            }`}
          >
            <FaArrowLeft size={24} />
          </button>

          <button
            onClick={() => scrollDirection('right')}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-4 rounded-full transition-all duration-300 ${
              showRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'
            } ${
              isLight 
                ? 'bg-gray-800 text-white hover:bg-gray-700'
                : 'bg-white text-gray-800 hover:bg-gray-100'
            }`}
          >
            <FaArrowRight size={24} />
          </button>

          {/* Testimonials Slider */}
          <motion.div
            ref={sliderRef}
            className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory"
            variants={containerVariants}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onTouchStart={() => setIsAutoPlaying(false)}
            onTouchEnd={() => setTimeout(() => setIsAutoPlaying(true), 5000)}
          >
            {testimonials.items.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex-none ${
                  isMobile 
                    ? 'w-[85vw] mr-4'
                    : 'w-full lg:w-1/2 xl:w-1/3'
                } p-4 snap-start`}
              >
                <div className={`h-full rounded-xl overflow-hidden transition-all duration-300 ${
                  isLight
                    ? 'bg-gray-800/50 hover:bg-gray-800/70'
                    : 'bg-white hover:bg-gray-50'
                } backdrop-blur-sm shadow-lg p-6`}
                >
                  {/* Project Type */}
                  <div className={`text-sm font-semibold mb-4 ${
                    isLight ? 'text-blue-400' : 'text-blue-600'
                  }`}>
                    {testimonial.project}
                  </div>

                  {/* Title */}
                  <h3 className={`text-xl font-bold mb-4 ${
                    isLight ? 'text-white' : 'text-gray-900'
                  }`}>
                    {testimonial.title}
                  </h3>

                  {/* Stars */}
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className="text-yellow-400 mr-1" />
                    ))}
                  </div>

                  {/* Testimonial */}
                  <p className={`mb-6 italic ${
                    isLight ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    "{testimonial.testimonial}"
                  </p>

                  {/* Client Info */}
                  <div className={`mb-6 ${
                    isLight ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <p className="font-semibold">{testimonial.client}</p>
                    <p className={`text-sm ${
                      isLight ? 'text-gray-400' : 'text-gray-500'
                    }`}>{testimonial.role}</p>
                  </div>

                  {/* Impact */}
                  <div className="space-y-2">
                    {testimonial.impacts.map((impact, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center text-sm ${
                          isLight ? 'text-gray-300' : 'text-gray-600'
                        }`}
                      >
                        <FaCheckCircle className={`mr-2 ${
                          isLight ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        {impact}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Slide Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {Array.from({ length: Math.ceil(totalSlides / slidesToShow) }).map((_, index) => (
              <button
                key={index}
                onClick={() => isMobile ? handleMobileSlideClick(index) : scrollToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  currentSlide === index 
                    ? isLight 
                      ? 'bg-blue-500 w-4' 
                      : 'bg-blue-600 w-4'
                    : isLight
                      ? 'bg-gray-600'
                      : 'bg-gray-300'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Trusted Brands */}
        <motion.div 
          className="mt-20"
          variants={containerVariants}
        >
          <motion.h3 
            className={`text-2xl font-bold text-center mb-10 ${
              isLight ? 'text-white' : 'text-gray-900'
            }`}
            variants={itemVariants}
          >
            Trusted By Leading Brands
          </motion.h3>
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8"
            variants={containerVariants}
          >
            {trustedBrands.map((brand, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                className={`flex flex-col items-center justify-center p-4 rounded-lg ${
                  isLight ? 'text-gray-400' : 'text-gray-600'
                }`}
              >
                {brand.icon}
                <span className="mt-2 text-sm font-medium">{brand.name}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div 
          className="text-center mt-16"
          variants={itemVariants}
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={testimonials.cta.primary.link}
              className={`inline-flex items-center px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 ${
                isLight 
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              } transform hover:scale-105 active:scale-95`}
            >
              {testimonials.cta.primary.text}
              <FaArrowRight className="ml-2" />
            </Link>
            <Link
              to={testimonials.cta.secondary.link}
              className={`inline-flex items-center px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 ${
                isLight 
                  ? 'bg-gray-800 text-white hover:bg-gray-700'
                  : 'bg-gray-700 text-white hover:bg-gray-600'
              } transform hover:scale-105 active:scale-95`}
            >
              {testimonials.cta.secondary.text}
              <FaArrowRight className="ml-2" />
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default TestimonialSection;